import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import voiceService, { DEFAULT_VOICE, VOICE_OPTIONS } from '../services/voiceService';
import authService, { AgentNumberConfig } from '../services/authService';
import whatsappSettingsService from '../services/whatsappSettingsService';
import twilioService from '../services/twilioService';
import whatsappService from '../services/whatsappService';
import Layout from '../components/Layout';
import VoiceTestCallDialog from '../components/VoiceTestCallDialog';
import AgentRulesSection from '../components/AgentRulesSection';
import AgentBehaviorSection from '../components/AgentBehaviorSection';
import HandoffSettingsCard from '../components/agents/HandoffSettingsCard';
import CallbackTicketSettingsCard from '../components/agents/CallbackTicketSettingsCard';
import AgentSectionCards, {
  AgentTab,
  WhatsAppAgentStatus,
} from '../components/agents/AgentSectionCards';
import {
  agentTabUrl,
  BehaviorTab,
  DEFAULT_AGENT_SECTION,
  DEFAULT_BEHAVIOR_TAB,
  parseAgentSection,
  parseBehaviorTab,
} from '../lib/agentDeepLink';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { CharCounter } from "@/components/ui/char-counter";
import {
  AGENT_NAME_MAX,
  VOICE_FAREWELL_MAX,
  VOICE_GREETING_MAX,
  WHATSAPP_GREETING_MAX,
} from "@/constants/contentLimits";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Volume2,
  User,
  Play,
  Square,
  Mic,
  MessageSquare,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const VoiceAgentPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();

  // `?section=phone|whatsapp|behavior|limits` (and `&sub=crosssell|rules`
  // inside Verhalten) open the page on exactly that setting — the assistant
  // links here. Unknown values fall back to the defaults.
  const [activeTab, setActiveTab] = useState<AgentTab>(
    () => parseAgentSection(new URLSearchParams(search).get('section')) ?? DEFAULT_AGENT_SECTION,
  );
  const [behaviorTab, setBehaviorTab] = useState<BehaviorTab>(
    () => parseBehaviorTab(new URLSearchParams(search).get('sub')) ?? DEFAULT_BEHAVIOR_TAB,
  );
  const [voiceModel, setVoiceModel] = useState(DEFAULT_VOICE);
  const [agentName, setAgentName] = useState('');
  const [greetingText, setGreetingText] = useState('');
  const [farewellText, setFarewellText] = useState('');
  const [whatsappGreeting, setWhatsappGreeting] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [voiceWebhookActive, setVoiceWebhookActive] = useState(false);
  const [hasTwilioNumber, setHasTwilioNumber] = useState(false);
  const [webhookLoading, setWebhookLoading] = useState(false);
  // WhatsApp-Anbindung des Unternehmens. `null` heisst "noch nicht bekannt"
  // bzw. "Abruf fehlgeschlagen" — bewusst nicht als "getrennt" behandelt.
  const [companyWhatsapp, setCompanyWhatsapp] = useState<{
    status: string;
    number?: string;
  } | null>(null);

  // Stage 1: "Vorhören" – kurzes TTS-Sample
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  // Stage 2: "Probegespräch" – Live-WebRTC-Dialog
  const [testCallOpen, setTestCallOpen] = useState(false);

  // Rückruf-Ticket: an address typed but not saved survives a tab switch
  // (the card unmounts with its tab).
  const [callbackEmailDraft, setCallbackEmailDraft] = useState<string | null>(null);

  // Agent switcher: a tenant can run several agent numbers, each serving a
  // subset of its salons. Every setting below belongs to the selected one.
  const [agents, setAgents] = useState<AgentNumberConfig[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  // Which fields still come from the company default rather than this agent.
  const [inherited, setInherited] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchSettings = async () => {
      try {
        // The agent list decides which settings we then load. Accounts with a
        // single number never see a switcher and behave exactly as before.
        const agentList = await authService
          .getNumberConfigs()
          .then((res) => res.numbers || [])
          .catch(() => [] as AgentNumberConfig[]);
        setAgents(agentList);
        const agentFromUrl = new URLSearchParams(window.location.search).get('agent');
        const initialAgent =
          agentList.length > 1
            ? agentList.find((a) => a.phone_number === agentFromUrl)?.phone_number ||
              agentList[0].phone_number
            : null;
        setSelectedAgent(initialAgent);

        const [settings, voiceStatus, waSettings, waStatus] = await Promise.all([
          voiceService.getSettings(initialAgent),
          twilioService.getVoiceStatus(),
          whatsappSettingsService.getSettings().catch(() => null),
          whatsappService.getStatus().catch((err) => {
            console.error('Error fetching WhatsApp status:', err);
            return null;
          }),
        ]);
        if (settings.success) {
          setVoiceModel(settings.voice_model || DEFAULT_VOICE);
          setAgentName(settings.agent_name || '');
          setGreetingText(settings.greeting_text || '');
          setFarewellText(settings.farewell_text || '');
          setInherited(settings.inherited || {});
        }
        if (waSettings?.success) {
          setWhatsappGreeting(waSettings.greeting_text || '');
        }
        setCompanyWhatsapp(
          waStatus ? { status: waStatus.status, number: waStatus.whatsapp_number } : null,
        );
        if (voiceStatus.success) {
          setHasTwilioNumber(voiceStatus.has_number);
          if (voiceStatus.has_number) {
            setVoiceWebhookActive(Boolean(voiceStatus.voice_enabled));
          }
        }
      } catch (err) {
        console.error('Error fetching voice settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [isAuthenticated, navigate]);

  const agentLabel = (agent: AgentNumberConfig): string =>
    agent.display_name || agent.phone_number;

  /**
   * WhatsApp-Status der GEWAEHLTEN Nummer.
   *
   * Die Statuszeile hing frueher nur am Unternehmens-Status. Bei mehreren
   * Agenten sagte sie dadurch dasselbe fuer eine Nummer, unter der WhatsApp
   * laeuft, und fuer eine, die gar keine WhatsApp-Anbindung hat.
   */
  const whatsappStatus: WhatsAppAgentStatus = useMemo(() => {
    if (!companyWhatsapp) return 'unknown';
    const companyConnected = companyWhatsapp.status === 'connected';
    // Vergleich ueber die Ziffern: "+49 611 76039733" und "+4961176039733"
    // sind dieselbe Leitung.
    const digits = (value?: string | null) => (value || '').replace(/\D/g, '');
    const agent = agents.find((a) => a.phone_number === selectedAgent);

    // Eigene WABA-Anbindung genau auf dieser Nummer.
    if (agent?.has_own_whatsapp) return 'connected';

    // Einzelnummer-Konto, oder die gewaehlte Nummer IST die WhatsApp-Leitung
    // des Unternehmens — dann ist der Unternehmensstatus auch ihr Status.
    if (!selectedAgent || digits(selectedAgent) === digits(companyWhatsapp.number)) {
      return companyConnected ? 'connected' : 'disconnected';
    }

    // Keine eigene Leitung: es antwortet die Anbindung des Unternehmens.
    return companyConnected ? 'inherited' : 'disconnected';
  }, [companyWhatsapp, agents, selectedAgent]);

  /**
   * Keep the section in the URL like the chosen agent: a reload (or a shared
   * link) reopens the same place. The router's own history entry is kept.
   */
  const syncSectionUrl = (section: AgentTab, tab: BehaviorTab) => {
    window.history.replaceState(window.history.state, '', agentTabUrl(window.location.href, section, tab));
  };

  const handleSectionChange = (section: AgentTab) => {
    setActiveTab(section);
    syncSectionUrl(section, behaviorTab);
  };

  const handleBehaviorTabChange = (tab: BehaviorTab) => {
    setBehaviorTab(tab);
    syncSectionUrl('behavior', tab);
  };

  /** Switch the whole page to another agent and reload its settings. */
  const handleAgentChange = async (phoneNumber: string) => {
    setSelectedAgent(phoneNumber);
    // Keep the choice in the URL so a reload (or a shared link) reopens the
    // same agent instead of silently jumping back to the first one.
    const url = new URL(window.location.href);
    url.searchParams.set('agent', phoneNumber);
    window.history.replaceState({}, '', url.toString());
    try {
      const settings = await voiceService.getSettings(phoneNumber);
      if (settings.success) {
        setVoiceModel(settings.voice_model || DEFAULT_VOICE);
        setAgentName(settings.agent_name || '');
        setGreetingText(settings.greeting_text || '');
        setFarewellText(settings.farewell_text || '');
        setInherited(settings.inherited || {});
      }
    } catch (err) {
      console.error('Error switching agent:', err);
      toast.error('Einstellungen dieses Agenten konnten nicht geladen werden');
    }
  };

  /** Note under a field that still shows the company-wide value. */
  const inheritedHint = (field: string): React.ReactNode =>
    inherited[field] && selectedAgent ? (
      <p className="text-xs text-muted-foreground">
        Standard des Unternehmens — gilt für alle Agenten, bis du ihn hier für{' '}
        <span className="font-medium">
          {agents.find((a) => a.phone_number === selectedAgent)?.display_name ||
            'diesen Agenten'}
        </span>{' '}
        überschreibst.
      </p>
    ) : null;

  // One save for all agent settings: phone (voice) settings incl. greeting +
  // farewell, and the WhatsApp greeting. Both tab "Speichern" buttons call this.
  const handleSave = async () => {
    setSaving(true);
    try {
      // Sequential on purpose: both endpoints read-modify-write the SAME
      // whatsapp_profile JSONB. Fired in parallel (the old Promise.all), the
      // later commit overwrote the earlier one — greeting/farewell texts
      // "saved" and then reverted to the examples on reload (Laurentius,
      // 29.07.). The backend now also row-locks, this keeps the requests from
      // ever contending in the first place.
      const result = await voiceService.updateSettings(
        {
          voice_model: voiceModel,
          agent_name: agentName || undefined,
          greeting_text: greetingText, // "" clears the custom greeting
          farewell_text: farewellText, // "" clears the farewell (standard goodbye)
        },
        // With an agent selected this stores an override for that agent only.
        selectedAgent,
      );
      const waResult = await whatsappSettingsService.updateSettings({
        greeting_text: whatsappGreeting,
      });
      if (result.success && waResult.success) {
        toast.success('Agenten-Einstellungen gespeichert');
      } else {
        toast.error(result.error || waResult.error || 'Fehler beim Speichern');
      }
    } catch (err) {
      console.error('Error saving agent settings:', err);
      toast.error('Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const stopPreview = () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
    }
    setPreviewPlaying(false);
  };

  useEffect(() => {
    // Beim Stimmenwechsel laufendes Sample stoppen und Blob-URL verwerfen.
    stopPreview();
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, [voiceModel]);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const handlePreviewVoice = async () => {
    if (previewPlaying) {
      stopPreview();
      return;
    }
    setPreviewLoading(true);
    try {
      if (!previewUrlRef.current) {
        const blob = await voiceService.fetchPreviewAudio(voiceModel);
        previewUrlRef.current = URL.createObjectURL(blob);
      }
      const audio = previewAudioRef.current ?? new Audio();
      previewAudioRef.current = audio;
      audio.src = previewUrlRef.current;
      audio.onended = () => setPreviewPlaying(false);
      audio.onerror = () => {
        setPreviewPlaying(false);
        toast.error('Vorhören fehlgeschlagen');
      };
      await audio.play();
      setPreviewPlaying(true);
    } catch (err) {
      console.error('Voice preview error:', err);
      toast.error('Vorhören konnte nicht geladen werden');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleWebhookToggle = async (checked: boolean) => {
    if (!hasTwilioNumber) {
      toast.error('Keine aktive Twilio-Nummer gefunden');
      return;
    }
    const previous = voiceWebhookActive;
    setVoiceWebhookActive(checked);
    setWebhookLoading(true);
    try {
      const response = checked
        ? await twilioService.enableVoice()
        : await twilioService.disableVoice();
      if (!response.success) {
        throw new Error('Umschalten fehlgeschlagen');
      }
      toast.success(
        checked
          ? 'Telefonagent aktiviert'
          : 'Telefonagent deaktiviert'
      );
    } catch (err) {
      console.error('Error toggling voice webhook:', err);
      setVoiceWebhookActive(previous);
      toast.error('Telefonagent konnte nicht umgeschaltet werden');
    } finally {
      setWebhookLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col justify-center items-center min-h-[60vh] gap-4">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-primary/25 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full absolute top-0 left-0 animate-spin"></div>
          </div>
          <p className="text-muted-foreground text-sm animate-pulse">Einstellungen werden geladen...</p>
        </div>
      </Layout>
    );
  }

  const selectedVoiceName = VOICE_OPTIONS.find((v) => v.id === voiceModel)?.name;

  return (
    <Layout>
      <div className="container max-w-4xl mx-auto py-8 space-y-8 animate-in fade-in duration-500">

        {/* Header */}
        <div className="border-b pb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Agenten</h1>
            <p className="text-muted-foreground mt-1">
              KI-Konfiguration für Telefon- &amp; WhatsApp-Agent
            </p>
          </div>

          {/* Only shown once a tenant actually runs more than one agent —
              single-number accounts see the page exactly as before. */}
          {agents.length > 1 && selectedAgent && (
            <div className="flex flex-col items-end gap-1">
              <select
                value={selectedAgent}
                onChange={(e) => handleAgentChange(e.target.value)}
                className="w-64 rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {agents.map((agent) => (
                  <option key={agent.phone_number} value={agent.phone_number}>
                    {agentLabel(agent)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground font-mono">{selectedAgent}</p>
            </div>
          )}
        </div>

        {/* Bereichsauswahl als Karten — gleicher Radius wie die Ecke, an der
            Seitenleiste und Inhaltsbereich zusammenlaufen. */}
        <AgentSectionCards
          active={activeTab}
          onSelect={handleSectionChange}
          phoneActive={voiceWebhookActive}
          phoneAvailable={hasTwilioNumber}
          voiceName={selectedVoiceName}
          whatsappStatus={whatsappStatus}
          whatsappCustomGreeting={Boolean(whatsappGreeting.trim())}
        />

        {activeTab === 'behavior' && (
          <Card
            id="agent-panel-behavior"
            role="tabpanel"
            aria-labelledby="agent-tab-behavior"
            className="shadow-sm border-border rounded-[28px] overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            <CardHeader>
              <CardTitle className="text-lg">Verhalten</CardTitle>
              <CardDescription>
                Wie Telefon- und WhatsApp-Agent in Gesprächen reagieren sollen — Cross-Selling und freie Prompt-Anweisungen.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AgentBehaviorSection
                agentNumber={selectedAgent}
                initialTab={behaviorTab}
                onTabChange={handleBehaviorTabChange}
              />
            </CardContent>
          </Card>
        )}

        {activeTab === 'limits' && (
          <Card
            id="agent-panel-limits"
            role="tabpanel"
            aria-labelledby="agent-tab-limits"
            className="shadow-sm border-border rounded-[28px] overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            <CardHeader>
              <CardTitle className="text-lg">Limits</CardTitle>
              <CardDescription>Budget, Blockliste und Ländervorwahlen für WhatsApp- & Telefonagent</CardDescription>
            </CardHeader>
            <CardContent>
              <AgentRulesSection agentNumber={selectedAgent} />
            </CardContent>
          </Card>
        )}

        {activeTab === 'whatsapp' && (
          // The tab panel holds both cards: greeting (+ its save) and the
          // handoff card, which saves on its own like cross-selling.
          <div
            id="agent-panel-whatsapp"
            role="tabpanel"
            aria-labelledby="agent-tab-whatsapp"
            className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
          <Card className="shadow-sm border-border rounded-[28px] overflow-hidden">
            <CardHeader>
              <CardTitle className="text-lg">WhatsApp-Agent</CardTitle>
              <CardDescription>Begrüßung und Verhalten deines WhatsApp-Agenten</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Which WhatsApp account answers under the selected agent —
                  its own, or the tenant's primary one. */}
              {selectedAgent && agents.length > 1 && (
                <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
                  {agents.find((a) => a.phone_number === selectedAgent)?.has_own_whatsapp ? (
                    <>
                      Dieser Agent antwortet über eine{' '}
                      <span className="font-medium">eigene WhatsApp-Anbindung</span>.
                      Begrüßung und Verhalten unten gelten nur für ihn.
                    </>
                  ) : (
                    <>
                      Dieser Agent nutzt die{' '}
                      <span className="font-medium">WhatsApp-Anbindung des Unternehmens</span>.
                      Eine eigene WhatsApp-Nummer verbindest du im Onboarding-Schritt
                      „Bestätigung"; danach erscheint sie hier als eigener Agent.
                    </>
                  )}
                </div>
              )}

              {/* WhatsApp Begrüßung */}
              <div className="space-y-2">
                <Label htmlFor="wa-greeting" className="flex items-center gap-2 font-medium">
                  <MessageSquare className="h-4 w-4 text-[#1967D2]" />
                  Begrüßung (wiederkehrende Kunden)
                </Label>
                <Textarea
                  id="wa-greeting"
                  placeholder="z.B. Hallo {name}! Schön, dass du wieder da bist. Womit kann ich dir helfen?"
                  value={whatsappGreeting}
                  onChange={(e) => setWhatsappGreeting(e.target.value)}
                  rows={3}
                  maxLength={WHATSAPP_GREETING_MAX}
                  className="bg-background"
                />
                <CharCounter value={whatsappGreeting} max={WHATSAPP_GREETING_MAX} />
                <p className="text-xs text-muted-foreground">
                  Wird an bereits registrierte Kunden (mit Datenschutz-Zustimmung) gesendet. Platzhalter <code>{'{name}'}</code> = Vorname. Leer lassen für die Standardbegrüßung. Die Erst-Begrüßung mit Namens- und Datenschutz-Abfrage bleibt aus rechtlichen Gründen fest.
                </p>
              </div>
              <p className="text-sm text-muted-foreground border-t pt-4">
                Regeln, die für den WhatsApp-Agenten gelten sollen, legst du im Tab „Verhalten" an (Schalter „WhatsApp-Agent" pro Regel). Technische Limits (Budget, Blockliste, Ländervorwahlen) im Tab „Limits".
              </p>
            </CardContent>
            <CardFooter className="bg-muted/50 border-t p-4 flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#1967D2] hover:bg-[#1558b0] text-white min-w-[140px]"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Speichern
              </Button>
            </CardFooter>
          </Card>
          <HandoffSettingsCard
            // Fresh card per agent: a late answer for agent A can never land in B's form.
            key={selectedAgent ?? 'company'}
            agentNumber={selectedAgent}
            agentLabel={agents.find((a) => a.phone_number === selectedAgent)?.display_name}
          />
          </div>
        )}

        {activeTab === 'phone' && (
          // The tab panel holds both cards, like the WhatsApp tab: the phone
          // settings (+ their save) and the Rückruf-Ticket card, which saves
          // on its own.
          <div
            id="agent-panel-phone"
            role="tabpanel"
            aria-labelledby="agent-tab-phone"
            className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
        <Card className="shadow-sm border-border rounded-[28px] overflow-hidden">
          <CardHeader>
            <CardTitle className="text-lg">Einstellungen</CardTitle>
            <CardDescription>Passen Sie Ihren KI-Telefonagenten an</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Voice Agent Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg border border-border">
              <div className="space-y-0.5">
                <Label htmlFor="voice-webhook-toggle" className="text-base font-medium text-foreground">
                  Telefonagent aktiv
                </Label>
                <p className="text-sm text-muted-foreground">
                  Aktiviert oder deaktiviert eingehende Anrufe mit dem Telefonagenten.
                </p>
                {!hasTwilioNumber && (
                  <p className="text-xs text-destructive-strong">
                    Keine aktive Twilio-Nummer gefunden.
                  </p>
                )}
              </div>
              <Switch
                id="voice-webhook-toggle"
                checked={voiceWebhookActive}
                onCheckedChange={handleWebhookToggle}
                disabled={!hasTwilioNumber || webhookLoading}
              />
            </div>

            {/* Agent Name */}
            <div className="space-y-2">
              <Label htmlFor="agent-name" className="flex items-center gap-2 font-medium">
                <User className="h-4 w-4 text-[#1967D2]" />
                Name des Telefonagenten
              </Label>
              <Input
                id="agent-name"
                type="text"
                placeholder="z.B. Lisa, Max, Emma"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                maxLength={AGENT_NAME_MAX}
                className="bg-background"
              />
              <p className="text-xs text-muted-foreground">
                Ihr Telefonagent stellt sich mit diesem Namen vor. Leer lassen für "die Terminassistenz".
              </p>
            </div>

            {/* Begrüßung */}
            <div className="space-y-2">
              <Label htmlFor="greeting-text" className="flex items-center gap-2 font-medium">
                <MessageSquare className="h-4 w-4 text-[#1967D2]" />
                Begrüßung
              </Label>
              <Textarea
                id="greeting-text"
                placeholder="z.B. Hallo {name}, schön dass du bei Salon Sonnenschein anrufst! Wie kann ich dir helfen?"
                value={greetingText}
                onChange={(e) => setGreetingText(e.target.value)}
                rows={3}
                maxLength={VOICE_GREETING_MAX}
                className="bg-background"
              />
              <CharCounter value={greetingText} max={VOICE_GREETING_MAX} />
              <p className="text-xs text-muted-foreground">
                Wird zu Beginn des Anrufs gesprochen. Der Platzhalter <code>{'{name}'}</code> wird durch den Vornamen ersetzt (bei bekannten Kunden). Leer lassen für die Standardbegrüßung.
              </p>
            </div>

            {/* Verabschiedung */}
            <div className="space-y-2">
              <Label htmlFor="farewell-text" className="flex items-center gap-2 font-medium">
                <Sparkles className="h-4 w-4 text-[#1967D2]" />
                Verabschiedung
              </Label>
              <Textarea
                id="farewell-text"
                placeholder="z.B. Erwähne zum Abschied unsere TimeGlobe-Karte: damit spart der Kunde 10% auf alle Produkte und Dienstleistungen."
                value={farewellText}
                onChange={(e) => setFarewellText(e.target.value)}
                rows={3}
                maxLength={VOICE_FAREWELL_MAX}
                className="bg-background"
              />
              <CharCounter value={farewellText} max={VOICE_FAREWELL_MAX} />
              <p className="text-xs text-muted-foreground">
                Der Telefonagent baut diese Anweisung in seine Verabschiedung ein, bevor er auflegt. Leer lassen für eine normale Verabschiedung.
              </p>
            </div>

            {/* Voice Selection */}
            <div className="space-y-2">
              <Label htmlFor="voice-select" className="flex items-center gap-2 font-medium">
                <Volume2 className="h-4 w-4 text-[#1967D2]" />
                Stimme auswählen
              </Label>
              <Select value={voiceModel} onValueChange={setVoiceModel}>
                <SelectTrigger id="voice-select" className="w-full bg-background h-auto py-3">
                  <SelectValue placeholder="Wählen Sie eine Stimme" />
                </SelectTrigger>
                <SelectContent>
                  {VOICE_OPTIONS.map((voice) => (
                    <SelectItem key={voice.id} value={voice.id} className="py-3 cursor-pointer">
                      <div className="flex flex-col gap-1 text-left">
                        <span className="font-semibold text-foreground">{voice.name}</span>
                        <span className="text-xs text-muted-foreground">{voice.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handlePreviewVoice}
                  disabled={previewLoading}
                >
                  {previewLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : previewPlaying ? (
                    <Square className="h-4 w-4 mr-2" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  {previewPlaying ? 'Stopp' : 'Stimme vorhören'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setTestCallOpen(true)}
                >
                  <Mic className="h-4 w-4 mr-2" />
                  Probegespräch starten
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                "Vorhören" spielt ein kurzes Sample ab. "Probegespräch" startet einen
                kurzen Live-Dialog im Browser – ohne echten Anruf und ohne Terminbuchung.
              </p>
            </div>

          </CardContent>
          <CardFooter className="bg-muted/50 border-t p-4 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1967D2] hover:bg-[#1558b0] text-white min-w-[140px]"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Speichern
            </Button>
          </CardFooter>
        </Card>
          <CallbackTicketSettingsCard
            agentNumber={selectedAgent}
            draftEmail={callbackEmailDraft}
            onDraftEmailChange={setCallbackEmailDraft}
          />
          </div>
        )}

        <VoiceTestCallDialog
          open={testCallOpen}
          onOpenChange={setTestCallOpen}
          voice={voiceModel}
          phoneNumber={selectedAgent}
        />
      </div>
    </Layout>
  );
};

export default VoiceAgentPage;
